<script setup>
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from "@headlessui/vue";
import { useMediaQuery, useToggle } from "@vueuse/core";
import algoliasearch from "algoliasearch/lite";
import { cloneDeep, isEmpty, isEqual, merge } from "lodash";
import { usePrimeVue } from "primevue/config";
import InputSwitch from "primevue/inputswitch";
import { computed, onBeforeMount, onMounted, reactive, ref, toRefs, unref, watch } from "vue";
import { AisConfigure, AisInstantSearch } from "vue-instantsearch/vue3/es";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import { badgeConvertMap, INITIAL_FILTERS } from "@/components/ExperiencePage/Algolia/AlgoliaSideBar/constants";
import { useConvertToUrlParams, useQueryParamsToState } from "@/components/ExperiencePage/Algolia/composables";
import FilterBadgeBar from "@/components/ExperiencePage/Algolia/FilterBadgeBar/index.vue";
import CoursesTab from "@/components/ExperiencePage/Algolia/Tabs/DevCoursesTab.vue";
import InternshipTab from "@/components/ExperiencePage/Algolia/Tabs/DevInternshipTab.vue";
import DevProgramsTab from "@/components/ExperiencePage/Algolia/Tabs/DevProgramsTab.vue";
import { useSearchClient } from "@/components/ExperiencePage/Algolia/useSearchClient";
import { buildFilterStrings2 } from "@/components/ExperiencePage/Algolia/utils";
import { inputSwitchPt } from "@/components/program-manager/sessions/components/PromotionalSettings/style.js";
import BreadCrumb from "@/components/shared/BreadCrumb.vue";
import FilterChevronIcon from "@/components/svg-icons/FilterChevronIcon.vue";
import FiltersIcon from "@/components/svg-icons/FiltersIcon.vue";
import { API_ENTITY_INFORMATION, FEATURES } from "@/constants.js";
import { useGetEntityStorefront } from "@/providers/entities/useGetEntityStorefront";
import PaginationHeader from "../AlgoliaPaginationHeader.vue";
import SideBar from "../AlgoliaSideBar";
import {
  ALGOLIA_PRESTATE_ADD_FILTER_NAMES,
  algoliaConfigurationMap,
  countriesTranslationBackMap,
  TAB_NAMES,
  TABS,
} from "../constants";
import EquivalencyBanner from "../EquivalencyBanner/index.vue";

const { searchClient } = useSearchClient();

const store = useStore();
const route = useRoute();
const router = useRouter();
const { config } = usePrimeVue();

const homeInstitutionId = computed(() => store.state.profileData?.colleges?.[0]?.college_id ?? "");

const domainEntityId = computed(() => store.getters.getDomainEntityId);

const tenancyDomainRestrictedMap = {
  sessionsDomainRestrictedId: domainEntityId.value,
  programsDomainRestrictedId: domainEntityId.value,
  classesVisibility: domainEntityId.value,
  programsPageOwnerEntityIdFilter: domainEntityId.value,
  sessionsOwnerEntityIdFilter: domainEntityId.value,
  internshipsOwnerEntityIdFilter: domainEntityId.value,
};

const tabComponentMap = {
  ProgramsTab: DevProgramsTab,
  InternshipTab: InternshipTab,
  CoursesTab: CoursesTab,
};

const state = reactive({
  filters: {
    ...INITIAL_FILTERS,
  },
  filtersUpdating: true,
});
const algoliaFilters = ref("");
const activeTab = ref(0);
const initialSearchState = ref({});

const preApprovedOnlyToggle = computed({
  get() {
    return !!route.query.preApprovedOnly;
  },
  set(newValue) {
    const query = { ...route.query };

    if (newValue) {
      query.preApprovedOnly = "true";
    } else {
      delete query.preApprovedOnly;
    }
    router.push({ query });
    searchInstance?.value?.setPage(0);
  },
});

const domainEntityIdIfDifferentOrUndefined = computed(() =>
  domainEntityId.value !== API_ENTITY_INFORMATION.id ? domainEntityId.value : undefined,
);

//if ci query param does not exist, default to domain.
const equivalencyEntityId = computed(() => {
  if (route?.query?.ci && route?.query?.ci !== API_ENTITY_INFORMATION.id) {
    return route?.query?.ci;
  } else if (domainEntityIdIfDifferentOrUndefined.value) {
    return domainEntityId.value;
  }
  return undefined;
});

const hideInternshipTab = computed(() => store.getters.getBooleanFeatureFlag(FEATURES.hideInternshipTab));

const filteredTabs = computed(() => {
  const isMaroonStore = /^maroon-store\./.test(window.location.hostname);
  return isMaroonStore && hideInternshipTab.value ? TABS.filter((tab) => tab.value !== "internships") : TABS;
});

const inClassesTab = computed(() => filteredTabs.value[activeTab.value]?.name === TAB_NAMES.COURSES);

const { data: equivalencyStorefront } = useGetEntityStorefront(equivalencyEntityId, {
  enabled: computed(() => !!equivalencyEntityId.value && inClassesTab.value),
});

const equivalencyOwnerName = computed(() => equivalencyStorefront.value?.name ?? "");

const hasPreApprovedCourses = computed(() => !!equivalencyStorefront.value?.has_pre_approved_courses);

const showEquivalency = computed(() => {
  const page = route?.path;
  const tab = TABS.find((tab) => tab.name === TAB_NAMES.COURSES);
  return (
    page === tab.slug && !!equivalencyEntityId.value && !!equivalencyOwnerName.value && hasPreApprovedCourses.value
  );
});
const algoliaFiltersWithEquivalency = computed(() => {
  return preApprovedOnlyToggle.value && showEquivalency.value
    ? `${algoliaFilters.value} AND class.equivalents.home_entity_id:${equivalencyEntityId.value}`
    : `${algoliaFilters.value}`;
});
const algoliaOptionalFiltersWithEquivalency = computed(() => {
  return showEquivalency.value ? `class.equivalents.home_entity_id:${equivalencyEntityId.value}<score=1>` : ``;
});

const [expanded, controlSection] = useToggle();

const profileId = computed(() => {
  return store.state.currentUser?.profile_id ?? "";
});

const profileData = computed(() => {
  return store.state.profileData;
});

const algoliaConfiguration = computed(() => {
  const tabIndex = unref(activeTab);
  return algoliaConfigurationMap[filteredTabs.value[tabIndex].value] || {};
});

const heading = computed(() => {
  return `${filteredTabs.value[activeTab.value].name} Search Results`;
});

const activeAlgoliaIndex = computed(() => {
  const currentTab = filteredTabs.value[activeTab.value];
  return currentTab ? currentTab.algoliaIndex : undefined;
});

const currentSearchIndex = computed(() => {
  return unref(searchClient).initIndex(unref(activeAlgoliaIndex));
});

const updateActiveTab = (activeTabIndex) => {
  const activeTabSelected = filteredTabs.value[activeTabIndex];
  if (activeTabSelected) {
    activeTab.value = activeTabIndex !== -1 ? activeTabIndex : 0;
    router.push({ path: activeTabSelected.slug, query: route.query });
    state.filtersUpdating = true;
  }
};

const removeOneFilter = (item) => {
  const newFilters = { ...state.filters };
  const { key, label, value } = item;
  if (key === "keyword") {
    newFilters[key] = "";
  } else if (key === "classTitle") {
    newFilters[key] = "";
  } else if (key === "price") {
    newFilters[key] = [];
  } else if (key === "pastPrograms") {
    newFilters[key] = false;
  } else if (key === "startDate" || key === "endDate") {
    newFilters[key] = undefined;
  } else if (key === "sessionId") {
    newFilters[key] = [];
  } else if (key === "terms" || key === "areaOfStudy") {
    newFilters[key] = newFilters[key].filter((val) => val !== value);
  } else if (key === "countries") {
    const lookedUpLabel = countriesTranslationBackMap?.[label] || label;
    newFilters[key] = newFilters[key].filter((val) => val !== lookedUpLabel);
  } else {
    newFilters[key] = newFilters[key].filter((val) => val !== label);
  }
  state.filters = newFilters;
};

const resetFilters = (persistedFilters = {}) => {
  const newFilters = {
    ...INITIAL_FILTERS,
  };
  state.filters = { ...newFilters, ...persistedFilters };
};

// generate url query string
const convertFiltersToUrlParams = (clonedFilters) => {
  const baseQuery = useConvertToUrlParams(clonedFilters);
  const ciParam = route.query.ci ? { ci: route.query.ci } : {};
  const newQuery = { ...baseQuery, ...ciParam };

  if (!isEmpty(newQuery)) {
    //push the queryString to the browsers url
    if (equivalencyEntityId.value) {
      newQuery.ci = equivalencyEntityId.value;
    }
    if (preApprovedOnlyToggle.value) {
      newQuery.preApprovedOnly = "true";
    }
    router.push({ query: newQuery, params: { savePosition: true } });
  } else {
    const fallbackQuery = route.query.ci ? { ci: route.query.ci } : {};
    router.push({ path: route.path, query: fallbackQuery });
  }
};
const updateFilters = (clonedFilters) => {
  if (clonedFilters) {
    const combinedTenancyFilters = merge(clonedFilters, tenancyDomainRestrictedMap);
    algoliaFilters.value = buildFilterStrings2(unref(algoliaConfiguration), combinedTenancyFilters);
  }
};

watch(
  () => state.filters,
  (newFilters, oldFilters) => {
    if (!isEqual(newFilters, oldFilters)) {
      const clonedFilters = cloneDeep(newFilters);
      updateFilters(clonedFilters);
      convertFiltersToUrlParams(clonedFilters);
    }
  },
  { deep: true, immediate: true },
);

watch(
  () => route.path,
  (newPath, oldPath) => {
    if (newPath !== oldPath) {
      const activeTabSlug = newPath;
      const activeTabIndex = filteredTabs.value.findIndex((tab) => tab.slug === activeTabSlug);
      activeTab.value = activeTabIndex !== -1 ? activeTabIndex : 0;
      const oldKeywords = state.filters?.keyword;
      resetFilters({ keyword: oldKeywords });
      updateFilters(state.filters);
      convertFiltersToUrlParams(state.filters);
      state.filtersUpdating = false;
    }
  },
  { immediate: true },
);

onBeforeMount(() => {
  const hasQueryParams = Object.keys(route.query).length > 0;
  if (hasQueryParams) {
    //make a search request if query params exist on mount
    state.filters = useQueryParamsToState(route, INITIAL_FILTERS);
  } else {
    resetFilters();
  }
  const clonedFilters = cloneDeep(state.filters);
  updateFilters(clonedFilters);

  const newInitialSearchState = {};
  newInitialSearchState[activeAlgoliaIndex.value] = { refinementList: {} };
  let foundValues = false;

  ALGOLIA_PRESTATE_ADD_FILTER_NAMES.forEach((selectName) => {
    const facetName = unref(algoliaConfiguration)[selectName];
    const filterValue = state.filters[selectName];
    if (
      facetName &&
      filterValue &&
      ((Array.isArray(filterValue) && filterValue?.length > 0) || !Array.isArray(filterValue))
    ) {
      newInitialSearchState[activeAlgoliaIndex.value].refinementList[facetName] = filterValue;
      foundValues = true;
    }
  });

  initialSearchState.value = foundValues ? newInitialSearchState : undefined;
});

onMounted(async () => {
  if (profileId.value && isEmpty(profileData.value)) {
    await store.dispatch("getProfile", profileId.value);
  }

  // Check if the screen width is less than or equal to a certain value (e.g., 768px)
  const isMobile = useMediaQuery("(max-width: 768px)");
  if (!isMobile) {
    expanded.value = true; // Initially expanded on non-mobile devices
  }

  if (store.state.currentUser) {
    await store.dispatch("getStudentApplications");
  }
});

const searchInstance = ref(undefined);
const searchFunction = (helper) => {
  searchInstance.value = helper;
  if (helper?.state?.disjunctiveFacets?.length > 0 && !state.filtersUpdating) {
    helper.search();
  }
};
</script>

<template>
  <h1 class="sr-only">{{ heading }}</h1>
  <ais-instant-search
    :search-client="searchClient"
    :index-name="activeAlgoliaIndex"
    :initial-ui-state="initialSearchState"
    :search-function="searchFunction"
  >
    <ais-configure
      :filters="algoliaFiltersWithEquivalency"
      :optionalFilters="algoliaOptionalFiltersWithEquivalency"
      :hits-per-page.camel="100"
    />

    <div id="results-container" class="font-montserrat md:flex">
      <section class="bg-gray-10">
        <BreadCrumb class="md:hidden pl-14 pt-1" custom-spacer experience-page
          >/
        </BreadCrumb>
        <header
          class="flex md:hidden items-center border-b border-gray-200 h-16 pl-16 pr-20"
        >
          <button
            aria-controls="expand-filters"
            :aria-expanded="expanded"
            class="flex items-center text-lg w-full justify-between text-indigo-base font-semibold"
            @click="controlSection()"
          >
            <span
              class="flex items-center text-base tracking-widest text-left uppercase"
            >
              <FiltersIcon />
              <h2 class="sr-only">Filters</h2>
            </span>
            <FilterChevronIcon />
          </button>
        </header>
        <div
          id="side-bar-menu"
          class="md:block bg-gray-75 md:w-72 xl:w-96 md:px-8"
          :class="{ hidden: !expanded }"
        >
          <SideBar
            v-model="state.filters"
            :algolia-configuration="algoliaConfiguration"
            :current-search-index="currentSearchIndex"
            @close-filters="controlSection"
            :domainEntityId="domainEntityIdIfDifferentOrUndefined"
          />
        </div>
      </section>
      <div id="results-content" class="w-full" role="region" aria-label="Search Results">
        <EquivalencyBanner v-if="showEquivalency" :owner-name="equivalencyOwnerName" />
        <div class="bg-white w-full px-2 py-8 sm:px-0">
          <TabGroup :selected-index="activeTab" @change="updateActiveTab">
            <TabList
              :class="`flex space-x border-0 sm:border-b ${primaryColorBorder} font-montserrat`"
            >
              <Tab
                v-for="tab in filteredTabs"
                :key="tab.name"
                v-slot="{ selected }"
                as="template"
              >
                <component
                  :is="selected ? 'h2' : 'div'"
                  class="flex-1 sm:flex-none focus:outline-none m-0 p-0 font-normal text-base leading-none"
                >
                  <button
                    :class="[
                      selected
                        ? `${primaryColorClassOutlinedNoHover} bg-white border-bottom-white`
                        : `${primaryColorClassBG} ${primaryColorBorder} text-white`,
                      'rounded-t-lg border-2  py-4 px-7 w-[266px] font-bold text-base mx-2 hidden sm:flex -m-px uppercase justify-center',
                    ]"
                  >
                    {{ tab.name }}
                  </button>
                  <button
                    :class="[
                      selected
                        ? `font-bold ${primaryColorBorder}`
                        : `${primaryColorBorder} font-medium`,
                      `${primaryColorClass} bg-white flex-1	whitespace-nowrap py-4 px-1 border-b-2 text-xs w-full sm:hidden border-r-2 boder-right-transparent uppercase`,
                    ]"
                  >
                    {{ tab.name }}
                  </button>
                </component>
              </Tab>
            </TabList>

            <div id="sorting-container" class="w-full px-5 lg:px-10">
              <div class="md:flex mb-5 justify-between mt-10 hidden md:block">
                <PaginationHeader />
                <div v-if="showEquivalency" class="flex justify-center items-center">
                  <span class="text-indigo-base">Show pre-approved courses only</span>
                  <InputSwitch
                    v-model="preApprovedOnlyToggle"
                    :pt="inputSwitchPt(config.pt.inputswitch.slider)"
                    class="ml-2"
                  />
                </div>
              </div>
              <div
                class="mt-10 md:mt-0 ls:pl-10 mb-9 flex flex-col md:flex-row md:justify-between md:items-center max-w-1310p"
              >
                <div class="flex mb-5 md:hidden justify-between">
                  <PaginationHeader />
                  <div v-if="showEquivalency" class="flex justify-center items-center">
                    <span class="text-indigo-base">Show pre-approved courses only</span>
                    <InputSwitch
                      v-model="preApprovedOnlyToggle"
                      :pt="inputSwitchPt(config.pt.inputswitch.slider)"
                      class="ml-2"
                    />
                  </div>
                </div>
                <FilterBadgeBar
                  :filters="state.filters"
                  :value-config="badgeConvertMap"
                  @clear-all="resetFilters"
                  @remove-one-filter="removeOneFilter"
                  :homeInstitutionId="homeInstitutionId"
                />
              </div>
              <hr class="w-full text-indigo-light mb-45p" />
            </div>
            <TabPanels
              :unmount="false"
              class="bg-white lg:px-10 gap-10 lg:flex lg:flex-col pb-18"
            >
              <TabPanel v-for="tab in filteredTabs" :key="tab.value">
                <component :is="tabComponentMap[tab.component]" />
              </TabPanel>
            </TabPanels>
          </TabGroup>
        </div>
      </div>
    </div>
  </ais-instant-search>
</template>
